import { z } from 'zod';

/**
 * The WebSocket envelope shared by all three sockets in the system:
 * agent → server, server → agent and server → dashboard.
 *
 * Every frame is a **flat** JSON object whose `event` field names it. Flat
 * rather than `{ event, data }` for two reasons:
 *
 *   - A discriminated union validates the whole frame in a single pass. With a
 *     nested payload the receiver would parse the envelope, look up a schema and
 *     parse again — two chances to forget the second step, and the second step is
 *     the one that guards untrusted content.
 *   - A truncated log line still shows which message it belonged to.
 *
 * `event` is the only field guaranteed to be present. A receiver that cannot
 * resolve `event` to a known schema MUST drop the frame; "handle it anyway" is
 * how an unlisted command would eventually get executed.
 */

/** Discriminator key. Declared once so no union can drift onto another field. */
export const WS_EVENT_KEY = 'event' as const;

/**
 * Just enough structure to route a frame and to log it when routing fails.
 *
 * Parsing with this schema proves nothing about the rest of the message — it is
 * a routing aid, never a substitute for the per-event schema.
 */
export const baseEnvelopeSchema = z.object({
  event: z.string().min(1).max(64),
});
export type BaseEnvelope = z.infer<typeof baseEnvelopeSchema>;

/**
 * Build one event schema: the caller's fields plus the literal `event` tag.
 *
 * The tag is applied last on purpose, so a caller cannot accidentally redefine
 * the discriminator with a field of its own and produce a union member that no
 * longer matches its own name.
 */
export function defineEventSchema<Event extends string, Shape extends z.ZodRawShape>(
  event: Event,
  shape: Shape,
) {
  return z.object({ ...shape, event: z.literal(event) });
}

/**
 * Build a discriminated union over `event`.
 *
 * This is the whole reason the envelope is flat and the tags are literals: on
 * the server→agent socket, *parsing with the union is the allowlist check*.
 * There is no second list of permitted event names to keep in sync, and therefore
 * no way for the two to disagree.
 */
export function eventUnion<
  Options extends [
    z.ZodDiscriminatedUnionOption<typeof WS_EVENT_KEY>,
    ...z.ZodDiscriminatedUnionOption<typeof WS_EVENT_KEY>[],
  ],
>(options: Options) {
  return z.discriminatedUnion(WS_EVENT_KEY, options);
}

/** Every event name carried by a message union. */
export type EventNameOf<Message extends BaseEnvelope> = Message['event'];

/** Narrow a message union to the single member named by `Event`. */
export type MessageForEvent<
  Message extends BaseEnvelope,
  Event extends EventNameOf<Message>,
> = Extract<Message, { event: Event }>;

/**
 * Read the event name off an untrusted frame without validating anything else.
 *
 * Returns `null` for a frame that is not an object or has no usable `event`,
 * which the caller must treat as "drop and count", not as an empty message.
 */
export function peekEventName(raw: unknown): string | null {
  const parsed = baseEnvelopeSchema.safeParse(raw);
  return parsed.success ? parsed.data.event : null;
}

/** True when `raw` carries an `event` matching one of `names`. */
export function isEventOfType<Names extends readonly string[]>(
  raw: unknown,
  names: Names,
): raw is { event: Names[number] } {
  const event = peekEventName(raw);
  return event !== null && (names as readonly string[]).includes(event);
}
